import request from '@/utils/request'

const baseURL = '/activity-logs'

/**
 * Activity Logs API Service
 * Provides methods to interact with the Activity Log API endpoints
 */
export default {
  /**
   * Get all activity logs with optional filters
   * @param {Object} params - Query parameters
   * @param {string} params.log_name - Filter by log name
   * @param {number} params.causer_id - Filter by user ID who performed the action
   * @param {string} params.subject_type - Filter by model type (e.g., "App\Models\Project\Project")
   * @param {number} params.subject_id - Filter by model ID
   * @param {string} params.event - Filter by event type: created, updated, deleted, restored
   * @param {string} params.from_date - Filter logs from this date (format: YYYY-MM-DD)
   * @param {string} params.to_date - Filter logs to this date (format: YYYY-MM-DD)
   * @param {number} params.per_page - Items per page (default: 15)
   * @param {number} params.page - Page number
   * @returns {Promise} API response with activity logs and pagination meta
   */
  list(params = {}) {
    return request({
      url: baseURL,
      method: 'get',
      params,
    })
  },

  /**
   * Get a single activity log by ID
   * @param {number} id - Activity log ID
   * @returns {Promise} API response with activity log data
   */
  show(id) {
    return request({
      url: `${baseURL}/${id}`,
      method: 'get',
    })
  },

  /**
   * Get activity logs for a specific model
   * @param {string} modelType - Model type identifier (e.g., "project", "customer", "employee")
   * @param {number} modelId - The ID of the model
   * @param {Object} params - Additional query parameters (per_page, page, etc.)
   * @returns {Promise} API response with activity logs for the model
   */
  getByModel(modelType, modelId, params = {}) {
    return request({
      url: `${baseURL}/model/${modelType}/${modelId}`,
      method: 'get',
      params,
    })
  },

  /**
   * Get activity logs for a specific user
   * @param {number} userId - The ID of the user
   * @param {Object} params - Additional query parameters (per_page, page, etc.)
   * @returns {Promise} API response with activity logs for the user
   */
  getByUser(userId, params = {}) {
    return request({
      url: `${baseURL}/user/${userId}`,
      method: 'get',
      params,
    })
  },

  /**
   * Get GPS positions from recent activity logs.
   * Default: latest position per (user_id, ip_address).
   * history=true: every activity log with GPS in the window (movement trail).
   * @param {import('./types').LastPositionsParams} params - Query parameters
   * @returns {Promise<import('./types').LastPositionsResponse>} API response
   */
  getLastPositions(params = {}) {
    return request({
      url: `${baseURL}/last-positions`,
      method: 'get',
      params,
    })
  },
}
